/**
 * PasskeyManagement Component
 *
 * Displays and manages user's passkey credentials in Security Settings.
 * Also includes the prompt modal for adding passkeys after login.
 */

"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { usePasskeys } from "@/hooks/usePasskeys";
import { BiometricType } from "@/lib/passkey-config";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "./Modal";
import { AnimatePresence, motion } from "framer-motion";

// SVG Icons for different biometric types
function FaceIdIcon(t0) {
  const $ = _c(10);
  const {
    className
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path d="M7 3H5C3.89543 3 3 3.89543 3 5V7" />;
    t2 = <path d="M17 3H19C20.1046 3 21 3.89543 21 5V7" />;
    t3 = <path d="M7 21H5C3.89543 21 3 20.1046 3 19V17" />;
    t4 = <path d="M17 21H19C20.1046 21 21 20.1046 21 19V17" />;
    t5 = <path d="M9 9V10" />;
    t6 = <path d="M15 9V10" />;
    t7 = <path d="M12 13V14" />;
    t8 = <path d="M8 16C8.5 17.5 10 18 12 18C14 18 15.5 17.5 16 16" />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
    $[7] = t8;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
    t4 = $[3];
    t5 = $[4];
    t6 = $[5];
    t7 = $[6];
    t8 = $[7];
  }
  let t9;
  if ($[8] !== className) {
    t9 = <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">{t1}{t2}{t3}{t4}{t5}{t6}{t7}{t8}</svg>;
    $[8] = className;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  return t9;
}
function TouchIdIcon(t0) {
  const $ = _c(3);
  const {
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path d="M92,128A156.12,156.12,0,0,1,72.5,203.56a12,12,0,1,1-21-11.64A132,132,0,0,0,68,128a60,60,0,0,1,99.68-45,12,12,0,1,1-15.88,18A36,36,0,0,0,92,128Zm84.16-8H176a12,12,0,0,0-12,11.8,226.76,226.76,0,0,1-17,82.84,12,12,0,1,0,22.19,9.13A250.42,250.42,0,0,0,188,132.2,12,12,0,0,0,176.15,120Zm-62.22,81.21a12,12,0,0,0-16,5.54c-1.37,2.81-2.85,5.65-4.38,8.42a12,12,0,1,0,21,11.63c1.74-3.14,3.41-6.35,5-9.55A12,12,0,0,0,113.93,201.21ZM128,116a12,12,0,0,0-12,12,181,181,0,0,1-4.75,41.26,12,12,0,0,0,8.94,14.42,11.79,11.79,0,0,0,2.75.32,12,12,0,0,0,11.67-9.26A204.55,204.55,0,0,0,140,128,12,12,0,0,0,128,116Zm0-96a108,108,0,0,0-31.5,4.67,12,12,0,0,0,7,23A84.06,84.06,0,0,1,212,128a277.39,277.39,0,0,1-6.84,61.34,12,12,0,0,0,9.05,14.36,11.61,11.61,0,0,0,2.66.3,12,12,0,0,0,11.7-9.34A301.89,301.89,0,0,0,236,128,108.12,108.12,0,0,0,128,20ZM68.47,51.74a12,12,0,0,0-17,0A107.32,107.32,0,0,0,20,128a83.26,83.26,0,0,1-7.61,35,12,12,0,1,0,21.81,10A107.11,107.11,0,0,0,44,128,83.42,83.42,0,0,1,68.5,68.71,12,12,0,0,0,68.47,51.74Z" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== className) {
    t2 = <svg className={className} stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg">{t1}</svg>;
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function WindowsHelloIcon(t0) {
  const $ = _c(4);
  const {
    className
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <circle cx="12" cy="8" r="4" />;
    t2 = <path d="M4 20C4 16.6863 7.58172 14 12 14C16.4183 14 20 16.6863 20 20" strokeLinecap="round" />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== className) {
    t3 = <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">{t1}{t2}</svg>;
    $[2] = className;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}
function PasskeyIcon(t0) {
  const $ = _c(7);
  const {
    className
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <circle cx="8" cy="8" r="5" />;
    t2 = <path d="M11.5 11.5L22 22" />;
    t3 = <path d="M18 18L22 22" />;
    t4 = <path d="M15 15L18 18" />;
    t5 = <path d="M19 19L21 17" />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t1 = $[0];
    t2 = $[1];
    t3 = $[2];
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  if ($[5] !== className) {
    t6 = <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">{t1}{t2}{t3}{t4}{t5}</svg>;
    $[5] = className;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
}

// Get the appropriate icon for the biometric type
export function BiometricIcon(t0) {
  const $ = _c(8);
  const {
    type,
    className
  } = t0;
  switch (type) {
    case "face-id":
      {
        let t1;
        if ($[0] !== className) {
          t1 = <FaceIdIcon className={className} />;
          $[0] = className;
          $[1] = t1;
        } else {
          t1 = $[1];
        }
        return t1;
      }
    case "touch-id":
      {
        let t1;
        if ($[2] !== className) {
          t1 = <TouchIdIcon className={className} />;
          $[2] = className;
          $[3] = t1;
        } else {
          t1 = $[3];
        }
        return t1;
      }
    case "windows-hello":
      {
        let t1;
        if ($[4] !== className) {
          t1 = <WindowsHelloIcon className={className} />;
          $[4] = className;
          $[5] = t1;
        } else {
          t1 = $[5];
        }
        return t1;
      }
    default:
      {
        let t1;
        if ($[6] !== className) {
          t1 = <PasskeyIcon className={className} />;
          $[6] = className;
          $[7] = t1;
        } else {
          t1 = $[7];
        }
        return t1;
      }
  }
}

// Format date for display
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}

/**
 * PasskeyManagement - Security Settings section
 */
export function PasskeyManagement() {
  const $ = _c(26);
  const {
    passkeys,
    hasPasskeys,
    isLoading,
    biometricType,
    biometricDisplayName,
    isPasskeySupported,
    supportDetails,
    registerPasskey,
    isRegistering,
    removePasskey,
    isRemoving
  } = usePasskeys();
  const [confirmRemove, setConfirmRemove] = useState(null);
  let t0;
  if ($[0] !== registerPasskey) {
    t0 = async () => {
      await registerPasskey();
    };
    $[0] = registerPasskey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleAddPasskey = t0;
  let t1;
  if ($[2] !== removePasskey) {
    t1 = async credentialId => {
      await removePasskey(credentialId);
      setConfirmRemove(null);
    };
    $[2] = removePasskey;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleRemovePasskey = t1;
  if (isLoading) {
    let t2;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="flex items-center justify-center p-8"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-gray-900" /></div>;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    return t2;
  }
  let t2;
  if ($[5] !== biometricType || $[6] !== confirmRemove || $[7] !== handleRemovePasskey || $[8] !== hasPasskeys || $[9] !== isRemoving || $[10] !== passkeys) {
    t2 = hasPasskeys && passkeys?.credentials ? <div className="space-y-3">{passkeys.credentials.map(credential => <div key={credential.id} className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-4"><div className="flex items-center gap-3"><div className="rounded-lg bg-gray-100 p-2"><PasskeyIcon className="h-5 w-5 text-gray-600" /></div><div><p className="text-[14px] font-medium text-gray-900">{credential.deviceName}</p><p className="text-[13px] text-gray-500">Added {formatDate(credential.createdAt)}</p></div></div>{confirmRemove === credential.id ? <div className="flex items-center gap-2"><button onClick={() => handleRemovePasskey(credential.id)} disabled={isRemoving} className="inline-flex h-8 items-center justify-center rounded-lg bg-red-500 px-3 text-[13px] font-medium text-white transition-colors hover:bg-red-600 disabled:opacity-50">{isRemoving ? "Removing..." : "Confirm"}</button><button onClick={() => setConfirmRemove(null)} className="text-[13px] font-medium text-gray-500 hover:text-gray-700">Cancel</button></div> : <button onClick={() => setConfirmRemove(credential.id)} className="text-[13px] font-medium text-red-500 hover:text-red-600">Remove</button>}</div>)}</div> : <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 p-6 text-center"><BiometricIcon type={biometricType} className="mx-auto mb-3 h-8 w-8 text-gray-400" /><p className="text-[13px] text-gray-500">No passkeys added yet</p><p className="mt-1 text-[12px] text-gray-400">Add one for faster, more secure login</p></div>;
    $[5] = biometricType;
    $[6] = confirmRemove;
    $[7] = handleRemovePasskey;
    $[8] = hasPasskeys;
    $[9] = isRemoving;
    $[10] = passkeys;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const t3 = !isPasskeySupported || isRegistering;
  let t4;
  if ($[12] !== biometricType) {
    t4 = <BiometricIcon type={biometricType} className="h-5 w-5" />;
    $[12] = biometricType;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const t5 = isRegistering ? "Setting up..." : `Add ${biometricDisplayName}`;
  let t6;
  if ($[14] !== handleAddPasskey || $[15] !== t3 || $[16] !== t4 || $[17] !== t5) {
    t6 = <button onClick={handleAddPasskey} disabled={t3} className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-xl bg-gray-900 text-[14px] font-semibold text-white transition-all duration-200 hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50">{t4}{t5}</button>;
    $[14] = handleAddPasskey;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== isPasskeySupported || $[20] !== supportDetails) {
    t7 = !isPasskeySupported && supportDetails && <div className="mt-2 text-center"><p className="text-[12px] text-gray-400">{supportDetails.reason || "Passkeys aren't available on this device or browser"}</p>{!supportDetails.isSecureContext && <p className="mt-1 text-[12px] text-amber-600">Tip: For local development, use http://localhost:3002</p>}</div>;
    $[19] = isPasskeySupported;
    $[20] = supportDetails;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t2 || $[23] !== t6 || $[24] !== t7) {
    t8 = <div className="space-y-3">{t2}{t6}{t7}</div>;
    $[22] = t2;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  return t8;
}

/**
 * PasskeySetupPromptModal - Shown after login to encourage passkey setup
 */
export function PasskeySetupPromptModal(t0) {
  const $ = _c(33);
  const {
    isOpen,
    onClose
  } = t0;
  const {
    biometricType,
    biometricDisplayName,
    registerPasskey,
    isRegistering,
    dismissPrompt
  } = usePasskeys();
  let t1;
  if ($[0] !== onClose || $[1] !== registerPasskey) {
    t1 = async () => {
      const success = await registerPasskey();
      if (success) {
        onClose();
      }
    };
    $[0] = onClose;
    $[1] = registerPasskey;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleSetup = t1;
  let t2;
  if ($[3] !== dismissPrompt || $[4] !== onClose) {
    t2 = async () => {
      await dismissPrompt();
      onClose();
    };
    $[3] = dismissPrompt;
    $[4] = onClose;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const handleDismiss = t2;
  let t3;
  let t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      scale: 0.8,
      opacity: 0
    };
    t4 = {
      scale: 1,
      opacity: 1
    };
    t5 = {
      duration: 0.3,
      ease: "easeOut"
    };
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t3 = $[6];
    t4 = $[7];
    t5 = $[8];
  }
  let t6;
  if ($[9] !== biometricType) {
    t6 = <motion.div initial={t3} animate={t4} transition={t5} className="mx-auto mb-4 w-fit rounded-full bg-gray-100 p-4"><BiometricIcon type={biometricType} className="h-12 w-12 text-gray-600" /></motion.div>;
    $[9] = biometricType;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== biometricDisplayName) {
    t7 = <h3 className="text-xl font-bold text-gray-900">Secure your account with {biometricDisplayName}</h3>;
    $[11] = biometricDisplayName;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <p className="mt-2 text-[13px] text-gray-500">Sign in faster without typing passwords. Your biometric data stays on your device.</p>;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t6 || $[15] !== t7) {
    t9 = <ModalHeader className="pt-8 text-center">{t6}{t7}{t8}</ModalHeader>;
    $[14] = t6;
    $[15] = t7;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <BenefitItem icon={<LightningIcon />} text="Lightning-fast login" />;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <BenefitItem icon={<ShieldIcon />} text="Phishing-resistant security" />;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ModalBody className="pt-4"><ul className="space-y-3">{t10}{t11}<BenefitItem icon={<DevicesIcon />} text="Works across your devices" /></ul></ModalBody>;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const t13 = isRegistering ? "Setting up..." : `Set up ${biometricDisplayName}`;
  let t14;
  if ($[20] !== handleSetup || $[21] !== isRegistering || $[22] !== t13) {
    t14 = <button onClick={handleSetup} disabled={isRegistering} className="inline-flex h-10 w-full items-center justify-center rounded-xl bg-gray-900 text-[14px] font-semibold text-white transition-all duration-200 hover:bg-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50">{t13}</button>;
    $[20] = handleSetup;
    $[21] = isRegistering;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== handleDismiss) {
    t15 = <button onClick={handleDismiss} className="text-[13px] font-medium text-gray-500 hover:text-gray-700">Not now</button>;
    $[24] = handleDismiss;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t14 || $[27] !== t15) {
    t16 = <ModalFooter className="flex flex-col gap-3 pb-6 pt-6">{t14}{t15}</ModalFooter>;
    $[26] = t14;
    $[27] = t15;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== isOpen || $[30] !== t16 || $[31] !== t9) {
    t17 = <Modal isOpen={isOpen} className="sm:max-w-md">{t9}{t12}{t16}</Modal>;
    $[29] = isOpen;
    $[30] = t16;
    $[31] = t9;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  return t17;
}

// Benefit icons
function LightningIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function ShieldIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function DevicesIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M3 5a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2.22l.123.489.804.804A1 1 0 0113 18H7a1 1 0 01-.707-1.707l.804-.804L7.22 15H5a2 2 0 01-2-2V5zm5.771 7H5V5h10v7H8.771z" clipRule="evenodd" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function BenefitItem(t0) {
  const $ = _c(7);
  const {
    icon,
    text
  } = t0;
  let t1;
  if ($[0] !== icon) {
    t1 = <span className="shrink-0 text-gray-600">{icon}</span>;
    $[0] = icon;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== text) {
    t2 = <span className="text-[14px]">{text}</span>;
    $[2] = text;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <li className="flex items-center gap-3 text-gray-700">{t1}{t2}</li>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}